import { useState, useEffect, useMemo } from 'react';

import * as pdfjs from 'pdfjs-dist';

import getIsCypressOnLocalhost from './getIsCypressOnLocalhost';

// Adapted from Mike Cousins' usePdf example:
// https://github.com/mikecousins/react-pdf-js/blob/master/src/index.tsx

// We hardcode the pdfjs version instead of pulling from the package.json b/c it will make src not longer be the rootDir
// see https://stackoverflow.com/questions/52121725/maintain-src-folder-structure-when-building-to-dist-folder-with-typescript-3#comment108812402_58941798
const pdfJsDistVersion = '2.5.207';
const pdfJsWorkerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfJsDistVersion}/pdf.worker.min.js`;

type TUsePDf = {
  canvasEl?: React.RefObject<HTMLCanvasElement | null>;
  file: string;
  scale?: number;
  rotate?: number;
  page?: number;
  cMapUrl?: string;
  cMapPacked?: boolean;
  withCredentials?: boolean;
  onError?: (reason: string) => void;
};

interface IPdf extends pdfjs.PDFDocumentProxy {
  _pdfInfo?: {
    numPages: number;
  };
}

const usePdf = ({
  canvasEl,
  file,
  scale = 1,
  rotate = 0,
  page = 1,
  cMapUrl,
  cMapPacked,
  withCredentials = false,
  onError = () => {},
}: TUsePDf) => {
  const [pdf, setPdf] = useState<IPdf>();

  useEffect(function onLoad() {
    pdfjs.GlobalWorkerOptions.workerSrc = pdfJsWorkerSrc;
  }, []);

  useEffect(
    function fetchPdf() {
      let isActive = true;
      if (file) {
        const config: pdfjs.PDFSource = { url: file, withCredentials };
        if (cMapUrl) {
          config.cMapUrl = cMapUrl;
          config.cMapPacked = cMapPacked;
        }

        pdfjs.getDocument(config).promise.then(
          (pdf) => {
            if (isActive) {
              setPdf(pdf);
            }
          },
          (reason) => {
            onError(reason);
          }
        );
      }
      return () => (isActive = false);
    } as () => void,
    [cMapPacked, cMapUrl, file, withCredentials]
  );

  useEffect(
    function handleChange() {
      if (canvasEl) {
        const drawPDF = (page: pdfjs.PDFPageProxy) => {
          const rotation = rotate === 0 ? page.rotate : page.rotate + rotate;
          const viewport = page.getViewport({ scale, rotation });
          const canvas = canvasEl.current;
          if (!canvas) {
            return;
          }
          const canvasContext = canvas.getContext('2d');
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          const renderContext = {
            canvasContext: canvasContext!,
            viewport,
          };
          page.render(renderContext);
        };
        if (pdf) {
          pdf.getPage(page).then(
            (p) => drawPDF(p),
            (e) => {
              if (!e.toString().includes('Invalid page request')) {
                throw Error(e);
              }
            }
          );
        }
      }
    },
    [pdf, page, scale, rotate, canvasEl]
  );

  const loading = useMemo(() => !pdf, [pdf]);
  const numPages = useMemo(() => (pdf ? pdf?._pdfInfo?.numPages : null), [pdf]);

  return [getIsCypressOnLocalhost() ? false : loading, numPages];
};

export default usePdf;
